import { z } from 'zod';

/**
 * RFC 9457 problem+json (doc 05 section 3).
 *
 * `code` is a STABLE machine-readable string and part of the public contract.
 * Adding a code is additive; changing what one means is breaking. Treat this
 * list the way you would treat a database migration.
 */

export const FieldErrorSchema = z.object({
  field: z.string(),
  code: z.string(),
  message: z.string(),
});
export type FieldError = z.infer<typeof FieldErrorSchema>;

export const ProblemSchema = z.object({
  type: z.string(),
  title: z.string(),
  status: z.number().int(),
  detail: z.string().optional(),
  instance: z.string().optional(),
  request_id: z.string(),
  code: z.string(),
  errors: z.array(FieldErrorSchema).optional(),
});
export type Problem = z.infer<typeof ProblemSchema>;

export const ERROR_CODES = {
  // auth
  AUTH_INVALID_CREDENTIALS: 'auth.invalid_credentials',
  AUTH_TOKEN_EXPIRED: 'auth.token_expired',
  AUTH_TOKEN_INVALID: 'auth.token_invalid',
  AUTH_MFA_REQUIRED: 'auth.mfa_required',
  AUTH_SESSION_REVOKED: 'auth.session_revoked',
  AUTH_REFRESH_REUSED: 'auth.refresh_reused',
  AUTH_ACCOUNT_LOCKED: 'auth.account_locked',
  AUTH_STEP_UP_REQUIRED: 'auth.step_up_required',

  // authz
  FORBIDDEN: 'authz.forbidden',
  OUT_OF_SCOPE: 'authz.out_of_scope',

  // generic
  NOT_FOUND: 'resource.not_found',
  VERSION_CONFLICT: 'resource.version_conflict',
  VALIDATION_FAILED: 'request.validation_failed',
  MALFORMED: 'request.malformed',
  RATE_LIMITED: 'request.rate_limited',
  IDEMPOTENCY_KEY_REUSED: 'idempotency.key_reused',
  INTERNAL: 'internal.error',
  DEPENDENCY_DOWN: 'internal.dependency_down',

  // domain
  TENANT_SLUG_TAKEN: 'tenant.slug_taken',
  TENANT_DOMAIN_TAKEN: 'tenant.domain_taken',
  TENANT_DOMAIN_IMMUTABLE: 'tenant.domain_immutable',
  EXTENSION_NUMBER_TAKEN: 'extension.number_taken',
  EXTENSION_IN_USE: 'extension.in_use',
} as const;

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];
